import { supabase } from '@foundation/altzero_base/base/utils/supabaseClient';
import type {
  IncidentWithDetails,
  IncidentNote,
  StatusHistoryEntry,
  CaseStatus,
  AssessmentFilters,
  PaginatedResponse,
  OutcomeKey,
} from '../types/assessmentHistory.types';
import type { IncidentRecord } from '../../incident-logging/types/incident.types';

// ── Helpers ─────────────────────────────────────────────────────────────────

const getDateRange = (preset: string): { from: string | null; to: string | null } => {
  const now = new Date();
  switch (preset) {
    case 'today': {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { from: start.toISOString(), to: null };
    }
    case 'this_week': {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const start = new Date(now.getFullYear(), now.getMonth(), diff);
      return { from: start.toISOString(), to: null };
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: start.toISOString(), to: null };
    }
    default:
      return { from: null, to: null };
  }
};

// ── List Incidents ──────────────────────────────────────────────────────────

export const fetchIncidents = async (
  filters: AssessmentFilters,
  page: number = 1,
  limit: number = 20,
  // When provided, restricts results to records belonging to this user (non-admin scope)
  userEmail?: string,
  userId?: string,
): Promise<PaginatedResponse<IncidentRecord & { current_status: CaseStatus }>> => {
  const offset = (page - 1) * limit;

  let query = supabase
    .schema('charlie')
    .from('incident_logs')
    .select('*', { count: 'exact' })
    .eq('incident_type', 'assessment')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  // Scope to user's own assessments (non-admin): match by user_id OR metadata.contact_email
  // user_id covers signed-in assessments; contact_email covers older records or anonymous flows
  if (userId || userEmail) {
    const orParts: string[] = [];
    if (userId) orParts.push(`user_id.eq.${userId}`);
    if (userEmail) orParts.push(`metadata->>contact_email.eq.${userEmail}`);
    query = query.or(orParts.join(','));
  }

  // Search by subject name
  if (filters.search) {
    query = query.ilike('subject_name', `%${filters.search}%`);
  }

  // Filter by outcome
  if (filters.outcome !== 'all') {
    query = query.eq('outcome', filters.outcome);
  }

  // Filter by date
  const dateRange = filters.dateFrom && filters.dateTo
    ? { from: filters.dateFrom, to: filters.dateTo }
    : getDateRange(filters.datePreset);

  if (dateRange.from) {
    query = query.gte('created_at', dateRange.from);
  }
  if (dateRange.to) {
    query = query.lte('created_at', dateRange.to);
  }

  // Filter by case status
  if (filters.status !== 'all') {
    query = query.eq('current_status', filters.status);
  }

  // Filter by venue
  if (filters.venueId) {
    query = query.eq('venue_id', filters.venueId);
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Failed to fetch incidents:', error);
    throw new Error(error.message);
  }

  const total = count ?? 0;

  return {
    data: (data ?? []) as (IncidentRecord & { current_status: CaseStatus })[],
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
    },
  };
};

// ── Fetch Recent (for HomePage) ─────────────────────────────────────────────

export const fetchRecentIncidents = async (
  limitCount: number = 5,
  userEmail?: string,
  userId?: string,
): Promise<(IncidentRecord & { current_status: CaseStatus })[]> => {
  let query = supabase
    .schema('charlie')
    .from('incident_logs')
    .select('*')
    .eq('incident_type', 'assessment')
    .order('created_at', { ascending: false })
    .limit(limitCount);

  if (userId || userEmail) {
    const orParts: string[] = [];
    if (userId) orParts.push(`user_id.eq.${userId}`);
    if (userEmail) orParts.push(`metadata->>contact_email.eq.${userEmail}`);
    query = query.or(orParts.join(','));
  }

  const { data, error } = await query;

  if (error) {
    console.error('Failed to fetch recent incidents:', error);
    throw new Error(error.message);
  }

  return (data ?? []) as (IncidentRecord & { current_status: CaseStatus })[];
};

// ── Fetch Single Incident with Details ──────────────────────────────────────

export const fetchIncidentDetail = async (
  incidentId: string,
): Promise<IncidentWithDetails> => {
  // Fetch incident, notes, and status history in parallel
  const [incidentResult, notesResult, statusResult] = await Promise.all([
    supabase
      .schema('charlie')
      .from('incident_logs')
      .select('*')
      .eq('id', incidentId)
      .single(),
    supabase
      .schema('charlie')
      .from('incident_notes')
      .select('*')
      .eq('incident_id', incidentId)
      .order('created_at', { ascending: true })
      .limit(50),
    supabase
      .schema('charlie')
      .from('incident_status_history')
      .select('*')
      .eq('incident_id', incidentId)
      .order('changed_at', { ascending: true })
      .limit(50),
  ]);

  if (incidentResult.error) {
    console.error('Failed to fetch incident:', incidentResult.error);
    throw new Error(incidentResult.error.message);
  }

  return {
    ...incidentResult.data,
    notes: (notesResult.data ?? []) as IncidentNote[],
    status_history: (statusResult.data ?? []) as StatusHistoryEntry[],
  } as IncidentWithDetails;
};

// ── Add Note ────────────────────────────────────────────────────────────────

export const addIncidentNote = async (
  incidentId: string,
  noteText: string,
  authorId: string,
): Promise<IncidentNote> => {
  const { data, error } = await supabase
    .schema('charlie')
    .from('incident_notes')
    .insert({
      incident_id: incidentId,
      author_id: authorId,
      note_text: noteText,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to add note:', error);
    throw new Error(error.message);
  }

  return data as IncidentNote;
};

// ── Update Case Status ──────────────────────────────────────────────────────

export const updateCaseStatus = async (
  incidentId: string,
  newStatus: CaseStatus,
  changedBy: string,
  note?: string,
): Promise<StatusHistoryEntry> => {
  // Update denormalized field + insert history in parallel
  const [updateResult, historyResult] = await Promise.all([
    supabase
      .schema('charlie')
      .from('incident_logs')
      .update({ current_status: newStatus })
      .eq('id', incidentId),
    supabase
      .schema('charlie')
      .from('incident_status_history')
      .insert({
        incident_id: incidentId,
        status: newStatus,
        changed_by: changedBy,
        note: note ?? null,
      })
      .select()
      .single(),
  ]);

  if (updateResult.error) {
    console.error('Failed to update incident status:', updateResult.error);
    throw new Error(updateResult.error.message);
  }

  if (historyResult.error) {
    console.error('Failed to insert status history:', historyResult.error);
    throw new Error(historyResult.error.message);
  }

  return historyResult.data as StatusHistoryEntry;
};

// ── Update Incident Fields ─────────────────────────────────────────────────

export const updateIncidentFields = async (
  incidentId: string,
  fields: { subject_name?: string | null; metadata?: Record<string, unknown> },
): Promise<void> => {
  const { error } = await supabase
    .schema('charlie')
    .from('incident_logs')
    .update(fields)
    .eq('id', incidentId);

  if (error) {
    console.error('Failed to update incident:', error);
    throw new Error(error.message);
  }
};

// ── Fetch Venues for filter dropdown ────────────────────────────────────────

export const fetchOrgVenues = async (): Promise<{ id: string; venue_name: string }[]> => {
  const { data, error } = await supabase
    .from('base_organisation_location')
    .select('id, venue_name')
    .eq('is_active', true)
    .order('venue_name');

  if (error) {
    console.error('Failed to fetch venues:', error);
    return [];
  }

  return (data ?? []) as { id: string; venue_name: string }[];
};
